import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { X, MapPin, Users, Dumbbell, LayoutGrid, ImageIcon, Upload, DollarSign, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { sportTypeAPI } from '../../services/api';
import { PricingPanel, formatMoney } from '../../payments/components/PaymentPriceModal';
import { uploadFile, uploadErrorMessage, handleUploadError } from '../../services/imageUploadService';

const CreateCourtModal = ({ isOpen, onClose, onSave, editingCourt = null, academyId }) => {
  const { theme } = useTheme();
  const isEditMode = !!editingCourt;
  // The panel reports its own list; this is only what the collapsed row shows.
  const [showPricing, setShowPricing] = useState(false);
  const [courtPrices, setCourtPrices] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    type: 'court',
    description: '',
    seatingCapacity: '',

    sportTypes: [],
    images: [],
  });

  const [sportTypes, setSportTypes] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [existingImageKeys, setExistingImageKeys] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch sport types
  useEffect(() => {
    const loadSportTypes = async () => {
      try {
        const response = await sportTypeAPI.getAll();
        setSportTypes(response?.data || response || []);
      } catch (error) {
        console.error('Failed to load sport types:', error);
      }
    };
    loadSportTypes();
  }, []);

  // Pre-fill form when editing
  useEffect(() => {
    if (editingCourt && isOpen) {
      setFormData({
        name: editingCourt.name || '',
        type: editingCourt.type || 'court',
        description: editingCourt.description || '',
        seatingCapacity: editingCourt.capacity != null ? String(editingCourt.capacity) : '',


        sportTypes: editingCourt.sportTypeIds || [],
        images: [],
      });
      setImagePreview([]);
      /* SEEDED FROM THE TAG THE COURT ALREADY CARRIES, so the collapsed row
       * can say what an hour costs before anybody opens it. Left to the panel's
       * own `onPricesChange`, a priced court read "Attach Price" until someone
       * expanded it — which is the same lie as showing nothing. */
      setCourtPrices(editingCourt.price ? [editingCourt.price] : []);
      setExistingImageKeys(Array.isArray(editingCourt.imageKeys) ? editingCourt.imageKeys : []);
    } else if (!isOpen) {
      handleClose();
    }
  }, [editingCourt, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSportTypeToggle = (sportTypeId) => {
    setFormData(prev => ({
      ...prev,
      sportTypes: prev.sportTypes.includes(sportTypeId)
        ? prev.sportTypes.filter(id => id !== sportTypeId)
        : [...prev.sportTypes, sportTypeId]
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imagePreview.length + existingImageKeys.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...newPreviews]);
    setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
  };

  const handleRemoveImage = (index) => {
    setImagePreview(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a court/field name');
      return;
    }
    if (formData.sportTypes.length === 0) {
      toast.error('Please select at least one sport type');
      return;
    }

    setUploading(true);
    try {
      // Upload any new images via Core API
      const newKeys = [];
      for (const file of formData.images) {
        try {
          const result = await uploadFile(file, 'group_assets');
          newKeys.push(result.key);
        } catch (err) {
          if (!handleUploadError(err)) {
            toast.error(uploadErrorMessage(err));
          }
          setUploading(false);
          return;
        }
      }

      const courtData = {
        name:         formData.name.trim(),
        type:         formData.type,
        description:  formData.description.trim() || null,
        capacity:     formData.seatingCapacity ? parseInt(formData.seatingCapacity, 10) : null,

        sportTypeIds: formData.sportTypes,
        imageKeys:    [...existingImageKeys, ...newKeys],
      };

      onSave(courtData);
      handleClose();
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', type: 'court', description: '', seatingCapacity: '', sportTypes: [], images: [] });
    setImagePreview([]);
    setExistingImageKeys([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className="sticky top-0 bg-inherit border-b p-6 flex items-center justify-between">
          <div>
            <h3 className={`text-xl font-semibold ${theme.text}`}>
              {isEditMode ? 'Edit Court/Field' : 'Add New Court/Field'}
            </h3>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              {isEditMode ? 'Update court/field information' : 'Add a new facility to your academy'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.textMuted}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className={`font-semibold ${theme.text}`}>Basic Information</h4>

            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                <MapPin size={16} className="inline mr-1" />
                Court/Field Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Main Tennis Court A"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-current focus:border-transparent`}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                <LayoutGrid size={16} className="inline mr-1" />
                Type *
              </label>
              <div className="flex gap-3">
                {['court', 'field'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: t }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors capitalize ${
                      formData.type === t
                        ? `${theme.tabActive} ${theme.borderAccent}`
                        : `${theme.border} ${theme.text} ${theme.cardHover} dark:hover:bg-blue-900/20`
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the court/field features, surface type, lighting, etc."
                rows="3"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-current focus:border-transparent`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                <Users size={16} className="inline mr-1" />
                Capacity
              </label>
              <input
                type="number"
                name="seatingCapacity"
                value={formData.seatingCapacity}
                onChange={handleChange}
                min="0"
                placeholder="e.g., 4"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-current focus:border-transparent`}
              />
            </div>

            {/* WHAT AN HOUR COSTS — THE SAME PANEL A SESSION IS PRICED WITH.
            
                Not a pair of fields of its own. A price is a row with a life:
                a name, a model, an audience, an on-sale window, who funds the
                service fee — and it is withdrawn and replaced rather than
                overwritten, because order lines point at the tag they were
                bought at. Two bare inputs here would have been a second, poorer
                way to say the same thing, disagreeing with the session editor
                the first time either changed.
            
                ⚠️ EDIT ONLY. A price hangs off the court, and a court being
                created has no id for it to hang off yet — the same reason a
                session is priced after it exists. */}
            <div className="sm:col-span-2">
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                <DollarSign size={16} className="inline mr-1" />
                Pricing
              </label>

              {!isEditMode ? (
                <p className={`text-sm ${theme.textMuted}`}>
                  Save the court first, then set what an hour costs.
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowPricing(v => !v)}
                    className={`w-full py-2 rounded-lg text-sm font-medium ${theme.borderAccent} border ${theme.link} ${theme.cardHover}  transition-colors flex items-center justify-center gap-1.5`}
                  >
                    <DollarSign className="w-4 h-4" />
                    {/* An hour with no tag is not free, it is unsellable —
                        `PaymentSellablesService.court()` refuses to price it —
                        so the label asks for one rather than showing nothing. */}
                    {courtPrices.length > 0
                      ? `Pricing — ${courtPrices.map(pr => `${formatMoney(pr.amount, pr.currency)}/hour`).join(' · ')}`
                      : 'Attach Price'}
                    <ChevronRight className={`w-4 h-4 transition-transform ${showPricing ? 'rotate-90' : ''}`} />
                  </button>

                  {showPricing && (
                    <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        What an hour costs when an open window names no price of its own.
                      </p>
                      <PricingPanel
                        academyId={academyId}
                        ownerType="court"
                        ownerId={editingCourt.id}
                        entityName={editingCourt.name}
                        onPricesChange={(list) => setCourtPrices(list.filter(pr => pr.isActive))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sport Types */}
          <div className="space-y-4">
            <h4 className={`font-semibold ${theme.text}`}>
              <Dumbbell size={18} className="inline mr-2" />
              Supported Sports *
            </h4>
            <p className={`text-sm ${theme.textSecondary}`}>
              Select all sports that can be played on this court/field
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {sportTypes.map(sport => (
                <button
                  key={sport.id}
                  type="button"
                  onClick={() => handleSportTypeToggle(sport.id)}
                  // Same shape as the court filter chips, and for the same
                  // reason: the hover was a hardcoded `bg-blue-50`, which on a
                  // dark or a custom-coloured academy is a near-white panel
                  // under `theme.text` — white on white, so the sport you were
                  // pointing at was the one you could not read. theme.cardHover
                  // is whatever "slightly raised" means in the theme actually
                  // on screen.
                  className={`px-4 py-3 rounded-lg border transition-colors text-left ${
                    formData.sportTypes.includes(sport.id)
                      ? `${theme.tabActive} border-transparent`
                      : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`
                  }`}
                >
                  <div className="font-semibold">{sport.sportName}</div>
                </button>
              ))}
            </div>
          </div>


          {/* Images (optional) */}
          <div className="space-y-4">
            <h4 className={`font-semibold ${theme.text}`}>
              <ImageIcon size={18} className="inline mr-2" />
              Images <span className={`text-sm font-normal ${theme.textMuted}`}>(optional)</span>
            </h4>
            <p className={`text-sm ${theme.textSecondary}`}>
              Add up to 5 images of the court/field (first image will be the cover)
            </p>

            {imagePreview.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {imagePreview.map((url, index) => (
                  <div key={index} className="relative group">
                    <img src={url} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    {index === 0 && (
                      <div className={`absolute top-2 left-2 px-2 py-1 ${theme.tabActive} text-xs rounded`}>Cover</div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing images from DB (edit mode) */}
            {existingImageKeys.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {existingImageKeys.map((key, idx) => (
                  <div key={key} className="relative group">
                    <img src={`/images/${key}`} alt={`Court image ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    {idx === 0 && imagePreview.length === 0 && (
                      <div className={`absolute top-2 left-2 px-2 py-1 ${theme.tabActive} text-xs rounded`}>Cover</div>
                    )}
                    <button
                      type="button"
                      onClick={() => setExistingImageKeys(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {existingImageKeys.length + imagePreview.length < 5 && (
              <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed ${theme.border} rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition`}>
                <Upload size={20} />
                <span>Upload Images ({existingImageKeys.length + imagePreview.length}/5)</span>
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className={`flex-1 px-4 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors disabled:opacity-60`}
            >
              {uploading ? 'Uploading…' : isEditMode ? 'Update Court/Field' : 'Add Court/Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCourtModal;
